/**
 * 核發政策（`activation_policy`）的**前端鏡像**——批93-C §2.2、批108 用語。
 *
 * ★★ 這裡算出來的一律只是**即時提示**：按鈕該寫「確認並核發」還是「確認送出（待審核）」、
 *   內容偏離模板時要不要跳「自訂內容需 superadmin 核發」。**伺服器才是權威**
 *   （`services/activation.py::policy_for` / `can_activate_directly`），前端算錯最多是
 *   文案不精準，不會放行任何東西——`confirm` 回應會帶回真正的 `policy` 與
 *   `policy_reason`，UI 以那個為準覆蓋。
 *
 * 語意提醒：`activation_policy` 的值是「**可免審直接核發的最低身分**」，不是「誰要審核」。
 *   `superadmin`＝只有 superadmin 能免審核發（其餘要走我方審核）
 *   `admin`＝我方 admin 以上可免審核發
 *   `reseller`＝代理商與 admin 以上皆可免審核發
 */
import type { PlanSpec, ProductSpec } from '@/api/ls'
import { optionOf, planByKey } from '@/lib/products'
import { LEGACY_COUNT_FIELDS } from '@/lib/seedQuotas'
import { MINUTES_PER_DAY, totalMinutes } from '@/lib/terms'
import { ROLE_LABELS } from '@/lib/roles'

export type ActivationPolicy = 'superadmin' | 'manager' | 'admin' | 'reseller'

/**
 * 政策嚴格度（數字大＝嚴）。用來取「快照與現行政策的較嚴者」。
 * ★ 批110 插入 `manager`（營運主管）——與後端 `services/activation.POLICY_RANK` 同一份階序。
 */
const STRICTNESS: Record<string, number> = { reseller: 1, admin: 2, manager: 3, superadmin: 4 }

/**
 * 內容偏離套裝模板時強制升到哪一級（批110＝`manager`；之前寫死 `superadmin`）。
 * 與後端 `activation.CUSTOM_CONTENT_POLICY` 是同一個值，改一邊要改兩邊。
 */
export const CUSTOM_CONTENT_POLICY: ActivationPolicy = 'manager'

/** 兩個政策取較嚴的那個（收緊立即生效、放寬不追溯——與後端 `max_strict` 同義）。 */
export function maxStrict(a: string | null | undefined, b: string | null | undefined): ActivationPolicy {
  const strictest = STRICTNESS.superadmin
  const sa = STRICTNESS[a ?? ''] ?? strictest
  const sb = STRICTNESS[b ?? ''] ?? strictest
  const worst = Math.max(sa, sb)
  return (Object.keys(STRICTNESS).find((k) => STRICTNESS[k] === worst) ?? 'superadmin') as ActivationPolicy
}

/** 這個角色能不能在這個政策下「免審直接核發」。未知角色一律 false（fail-closed）。 */
export function canActivateDirectly(
  role: string | null | undefined,
  policy: string | null | undefined,
): boolean {
  // 代理商不在 rank 內（它不是 admin 的子集），單獨判；其餘我方角色一律比階序。
  if (role === 'reseller') return policy === 'reseller'
  const mine = STRICTNESS[role ?? '']
  const need = STRICTNESS[policy ?? ''] ?? STRICTNESS.superadmin
  // `operator` 不在 STRICTNESS 裡 → undefined → false（它連 require_admin 都過不了）。
  return mine !== undefined && mine >= need
}

/**
 * 申請／核發表單目前的內容（只取會影響政策判定的欄位）。
 *
 * ★ 批114：**沒有具名的一級數字欄**。所有核發類表單現在只送 `quotas`，但打開舊草稿／
 *   以舊證為底換發時，content 仍可能帶著後端相容輸出的一級欄位——那些鍵名集中在
 *   `lib/seedQuotas.ts::LEGACY_COUNT_FIELDS`，本檔一個產品字面都不寫，所以這裡用
 *   索引簽章收下它們（見 `contentMatchesPlan` 的取值鏈）。
 */
export interface ActivationContent {
  modules?: Record<string, string> | null
  quotas?: Record<string, number | null> | null
  term_days?: number | null
  /** 批108：期限的分鐘餘數。**比對一律用總和**（見 `contentMatchesPlan`）。 */
  term_minutes?: number | null
  /**
   * 交付形態。★ 批108 起**不是偏離判準**（後端 `content_matches_plan` 的 docstring：同一個套裝
   * 賣給線上站與離線站是同一份商品）——欄位留著只是讓呼叫端照原樣傳，比對時不看它。
   */
  deployment_mode?: string | null
  /** 批102 V7：關掉 trial＝同一個模板簽出一張正式證。 */
  trial?: boolean | null
  /** 批102 V7：`true` 時到期日沿用現行那張（＝可能無限延長）。 */
  keep_expiry?: boolean | null
  /** 舊形狀的一級數字欄（鍵名見 `LEGACY_COUNT_FIELDS`）；具名欄位一律在上面宣告。 */
  [legacyCountField: string]: unknown
}

/**
 * `keep_expiry` 沒給值時伺服器端會用的預設——**與後端
 * `services/activation.py::default_keep_expiry` 是同一份計算**。
 * `change`（付費變更）＝沿用現行到期日；其餘（`new`／`renew`／`addon`）＝重新起算。
 */
export function defaultKeepExpiry(kind: string | null | undefined): boolean {
  return kind === 'change'
}

// ---------------------------------------------------------------------------
// 內容 vs 模板——後端 `services/activation.py` 的**逐行鏡像**（`_given`／`_term_allowed`／
// `content_matches_plan`）。開站內容閘（`lib/hostedContent.ts`）也重用這一支，與後端
// `hosted_content_reason` 重用 `content_matches_plan` 同一個結構：規則只有一份，不會再分岔。
// ---------------------------------------------------------------------------

/** 兩個一級相容鍵（席次／產品線上限）。鍵名集中在 `lib/seedQuotas.ts`，本檔不寫產品字面。 */
const LEGACY_PAIRS: [field: string, key: string][] = Object.entries(LEGACY_COUNT_FIELDS)
const LEGACY_KEYS = new Set(Object.values(LEGACY_COUNT_FIELDS))

/**
 * 後端 `_given`：這個欄位算不算「呼叫端顯式覆寫了模板」。
 * `null`／`undefined` 不算；空的物件／陣列也不算（「沒勾任何模組」與「沒送」長得一樣）；
 * 其餘一律算——`0`、`false`、空字串都是**有意義的值**，不可用 truthy 判定。
 */
export function given(value: unknown): boolean {
  if (value == null) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value as object).length > 0
  return true
}

/** 是一包物件（Python 的 `Mapping`；陣列不算）。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

/** 後端 `quota_compat._int_or_none`：整數就回它，其餘（含 `null`／小數／字串／布林）一律 `null`。 */
function intOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

/** 兩包 map 逐鍵全等（Python 的 `dict == dict`；`null` 與鍵缺席是兩件事）。 */
function sameMap(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a).sort()
  const kb = Object.keys(b).sort()
  if (ka.length !== kb.length) return false
  return ka.every((k, i) => kb[i] === k && a[k] === b[k])
}

/** 排除兩個一級相容鍵（它們另外逐一比過，見 `contentMatchesPlan`）。 */
function withoutLegacyCounts(m: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(m).filter(([k]) => !LEGACY_KEYS.has(k)))
}

/**
 * 產品可選期限（**總分鐘**）——後端 `products.term_options_of` 的鏡像：`term_options`（分鐘）
 * 非空就用它，否則 `term_day_options`（天 ×1440）。兩者都走 `optionOf` 的兜底（值是 `null`／
 * 缺鍵才兜底；**明確給空陣列＝沒有選項**）。
 *
 * ⚠️ 與 `lib/products.ts::termMinuteOptions` 不同：那支是**下拉選單**用的，清單空了會退回
 *   兜底值（畫面上總要有選項）；這支是**政策判準**，必須與後端逐字相同。
 */
export function termOptionsOf(spec: ProductSpec | null | undefined): number[] {
  const raw: unknown = optionOf(spec, 'term_options')
  const mins = Array.isArray(raw) ? raw.map(Number).filter((n) => Number.isFinite(n) && n > 0) : []
  if (mins.length) return [...new Set(mins.map(Math.trunc))].sort((a, b) => a - b)
  const legacy: unknown = optionOf(spec, 'term_day_options')
  const days = Array.isArray(legacy) ? legacy.map(Number).filter((n) => Number.isFinite(n) && n > 0) : []
  return [...new Set(days.map((d) => Math.trunc(d) * MINUTES_PER_DAY))].sort((a, b) => a - b)
}

/**
 * 期限是否落在「模板期限 ∪ 產品期限選項」內（後端 `_term_allowed`）。兩欄都沒給＝不比。
 *
 * 期限比其他欄位寬鬆：同一個套裝賣 30 天與 365 天是同一份 entitlement（差別只有錢），
 * 模組／配額改了才是另一份商品。★★ 批108：一律比**總分鐘**——`(3 天, 0 分)` 與
 * `(0 天, 4320 分)` 是同一個期限，逐欄比會把等價寫法判成偏離。
 */
export function termAllowed(
  spec: ProductSpec | null | undefined,
  plan: PlanSpec,
  termDays: number | null | undefined,
  termMinutes: number | null | undefined,
): boolean {
  if (!given(termDays) && !given(termMinutes)) return true
  const allowed = new Set<number>(termOptionsOf(spec))
  if (plan.term_days != null || plan.term_minutes) allowed.add(totalMinutes(plan.term_days, plan.term_minutes))
  return allowed.has(totalMinutes(termDays, termMinutes))
}

/**
 * 一級相容鍵的取值鏈（後端 `content_matches_plan` 開頭那一段）：**一級鍵 ＞ `quotas` 裡的同名鍵**。
 *
 * - 一級鍵 `given` → 有給，值就是它（`null` 是「沒指定」＝舊草稿由伺服器從模板展開）。
 * - 否則 `quotas` 裡**有這個鍵就算有給，值是 `null` 也算**——那是操作者勾了「無上限」，會原樣
 *   簽進憑證；當成「沒給」會讓「照模板直發」放行一張不限量的證（批114 修掉的政策破口）。
 * - 兩邊都沒有 → 沒給 → 不比（直發時由伺服器從模板展開，本來就等於模板）。
 */
export function legacyCount(
  content: Record<string, unknown>,
  field: string,
  key: string,
): { given: boolean; value: unknown } {
  if (given(content[field])) return { given: true, value: content[field] }
  const quotas = isRecord(content.quotas) ? content.quotas : {}
  if (key in quotas) return { given: true, value: quotas[key] }
  return { given: false, value: null }
}

/**
 * 內容是不是**等於**套裝模板（後端 `content_matches_plan` 的逐行鏡像）。
 *
 * ★ 這條是政策的核心不變式（覆核 B1）：「政策放行的免審核發，內容必須等於套裝模板」。
 *   否則代理商只要挑一個 `reseller` 級的試用套裝、把某個配額改成 500，就繞過了整套核准。
 *
 * 判準（**只比呼叫端顯式給了的欄位**，沒給的直發時由伺服器從模板展開）：
 * 1. 兩個一級相容鍵：見 `legacyCount`；給了就必須等於模板的 `quotas` 值（模板沒寫＝`null`）。
 * 2. 模組：**非空才比**（`{}` 簽出去是「沒有模組」，只會少不會多）。
 * 3. 其餘配額：`quotas` **是一包物件就比**（批151：`{}` 是「明確的空」，不是「沒給」——簽發路徑
 *    沒有一條把 `{}` 展開成模板）；`null`／沒送＝不比。排除兩個一級鍵後整包比，模板沒寫的鍵
 *    （含明確 `null`）與少了模板有的鍵都算偏離。
 * 4. `trial`：給了就要等於套裝的旗標；`keep_expiry`：給了就要等於 `defaultKeepExpiry(kind)`。
 * 5. 期限：`termAllowed`。
 * ★ `deployment_mode` 批108 起**不比**（見 `ActivationContent.deployment_mode`）。
 */
export function contentMatchesPlan(
  spec: ProductSpec | null | undefined,
  plan: PlanSpec | null | undefined,
  content: ActivationContent,
  kind?: string | null,
): boolean {
  if (!plan) return false
  const row = content as Record<string, unknown>
  const planQuotas: Record<string, unknown> = isRecord(plan.quotas) ? plan.quotas : {}
  for (const [field, key] of LEGACY_PAIRS) {
    const g = legacyCount(row, field, key)
    if (!g.given) continue
    // 模板側一律讀 `quotas`（後端 `quota_compat.plan_counts`；`plan_out` 的一級鏡像欄也是它推導的）。
    if (g.value !== intOrNull(planQuotas[key])) return false
  }
  if (given(content.modules) && !sameMap(content.modules as Record<string, unknown>, plan.modules ?? {})) {
    return false
  }
  if (isRecord(content.quotas)) {
    // 11-01：加購模板的 null＝「不設定」（展開時丟掉），比對基準跟著丟（後端 `content_matches_plan` 鏡像）。
    // 只丟模板側：申請內容帶 null 仍是偏離。
    const base = plan.quotas == null ? {} : withoutLegacyCounts(planQuotas)
    const baseForKind =
      kind === 'addon' ? Object.fromEntries(Object.entries(base).filter(([, v]) => v !== null)) : base
    if (!sameMap(withoutLegacyCounts(content.quotas), baseForKind)) return false
  }
  if (given(content.trial) && Boolean(content.trial) !== Boolean(plan.trial)) return false
  if (given(content.keep_expiry) && Boolean(content.keep_expiry) !== defaultKeepExpiry(kind)) return false
  return termAllowed(spec, plan, content.term_days, content.term_minutes)
}

export interface PolicyResult {
  policy: ActivationPolicy
  /** 為什麼被升為 superadmin；`custom_content`＝內容偏離套裝模板、`no_plan`＝沒掛套裝。 */
  reason: 'plan' | 'custom_content' | 'no_plan'
}

/**
 * 解析這份內容的核發政策（順序與後端 `policy_for` 相同）：
 *   ① 有選套裝且內容 == 模板 → 該套裝的 `activation_policy`
 *   ② 有選套裝但內容被改過 → **強制 `superadmin`**（`custom_content`）
 *   ③ 沒選套裝（客製／沿用無套裝舊證／客製加購）→ 產品的 `default_activation_policy`
 *      （後台 UI 不提供把它設成 `reseller`——無模板可比的東西不對外部角色放行）
 */
export function policyFor(
  spec: ProductSpec | null | undefined,
  planKey: string | null | undefined,
  content: ActivationContent,
  kind?: string | null,
): PolicyResult {
  const plan = planByKey(spec, planKey)
  if (!plan) {
    return {
      policy: (optionOf(spec, 'default_activation_policy') as ActivationPolicy) ?? 'superadmin',
      reason: 'no_plan',
    }
  }
  if (!contentMatchesPlan(spec, plan, content, kind)) {
    // 批110：升到 `CUSTOM_CONTENT_POLICY`，但**只升不降**（套裝本身就是 superadmin
    // 政策時，客製它仍然要 superadmin）——與後端 `policy_for` 的 `max_strict` 一致。
    const base = (plan.activation_policy as ActivationPolicy) ?? 'superadmin'
    return { policy: maxStrict(base, CUSTOM_CONTENT_POLICY), reason: 'custom_content' }
  }
  return { policy: (plan.activation_policy as ActivationPolicy) ?? 'superadmin', reason: 'plan' }
}

/** 送出鈕的文字：政策放行＝當場簽發，要講清楚。 */
export function confirmButtonLabel(canDirect: boolean): string {
  return canDirect ? '確認並核發' : '確認送出（待審核）'
}

/**
 * 表單上那句即時提示；null＝沒有需要提醒的事。
 *
 * ★ 批113（UI 走查第 7 點）：這句話有三個宿主（客戶頁的建立表單、申請詳情、代理商送件表單），
 *   各自的按鈕叫「建立草稿」／「免審核發」／「確認並核發」——原本寫死「按下『確認並核發』」
 *   在客戶頁是**指向一顆不存在的按鈕**。改成不綁按鈕名的中性措辭，三個宿主都成立。
 */
export function policyHint(result: PolicyResult, canDirect: boolean): string | null {
  if (canDirect) return '此內容依套裝的核發政策可免審直接核發——送出即當場簽發授權。'
  if (result.reason === 'custom_content') {
    return `自訂內容需${ROLE_LABELS[result.policy] ?? result.policy}核發：目前的模組／配額／期限與所選套裝的模板不一致，確認送出後會進入待審核。`
  }
  if (result.reason === 'no_plan') {
    return '未掛套裝的客製內容一律需我方審核——確認送出後會進入待審核。'
  }
  return '此套裝需我方審核——確認送出後會進入待審核。'
}
